/**
 * TestNode - For capturing and testing data flow
 *
 * A simple node that captures all inputs for testing/debugging.
 *
 * Endpoints:
 * - input (input): receives any value
 * - output (output): echoes the input
 * - trigger (input): receives trigger events
 */

import { BaseNode } from '@nodes/BaseNode';
import { Domain } from '@nodes/Endpoint';
import { events, params, triggers } from '@nodes/TypedEndpoints';
import type { Dependencies } from '@nodes/Dependencies';
import { NodeDef } from '@runtime/IRuntime';
import { Payload } from '@graph/Payload';

interface ReceivedValue {
  endpointId: string;
  value: any;
  time?: number;
  timestamp: number;
}

const endpoints = {
  ...events({
    in: { isInput: true, primary: true },
    out: { isOutput: true },
    event: { isInput: true },
  }),
  ...triggers({
    trigger: { isInput: true },
  }),
  ...params({
    pattern: { dataType: 'array', isInput: true },
  })
};

export class TestNode extends BaseNode<typeof endpoints> {
  private received: ReceivedValue[] = [];
  private inputCallback?: (endpointId: string, value: any, time?: number) => void;

  constructor(id: string, deps: Dependencies) {
    super(id, deps);
  }

  handleEvent(payload: Payload, endpointId: string): void {
    this.recordInput(endpointId, payload, payload?.time);
    // Echo 'in' events to output
    if (endpointId === 'in') {
      this.events.out.emit(payload);
    }
  }

  onParamChanged(payload: Payload, endpointId: string): void {
    this.recordInput(endpointId, payload.value, payload.time);
  }

  private recordInput(id: string, value: any, time?: number): void {
    this.received.push({
      endpointId: id,
      value,
      time,
      timestamp: Date.now()
    });
    this.inputCallback?.(id, value, time);
  }

  // ─────────────────────────────────────────────────────────────
  // Test API
  // ─────────────────────────────────────────────────────────────

  /**
   * Get all received values
   */
  getReceived(): ReceivedValue[] {
    return [...this.received];
  }

  /**
   * Get values received on a specific endpoint
   */
  getReceivedOn(endpointId: string): ReceivedValue[] {
    return this.received.filter(r => r.endpointId === endpointId);
  }

  /**
   * Get the last received value
   */
  getLastReceived(): ReceivedValue | undefined {
    return this.received[this.received.length - 1];
  }

  /**
   * Get the last received value on a specific endpoint
   */
  getLastReceivedOn(endpointId: string): ReceivedValue | undefined {
    const onEndpoint = this.getReceivedOn(endpointId);
    return onEndpoint[onEndpoint.length - 1];
  }

  /**
   * Get count of received values
   */
  getReceivedCount(): number {
    return this.received.length;
  }

  /**
   * Get count of values received on a specific endpoint
   */
  getReceivedCountOn(endpointId: string): number {
    return this.received.filter(r => r.endpointId === endpointId).length;
  }

  /**
   * Clear received values
   */
  clearReceived(): void {
    this.received = [];
  }

  /**
   * Set a callback to be called on each input
   */
  onReceive(callback: (endpointId: string, value: any, time?: number) => void): void {
    this.inputCallback = callback;
  }

  /**
   * Wait for a specific number of values to be received
   */
  waitFor(count: number, timeout = 5000): Promise<ReceivedValue[]> {
    return new Promise((resolve, reject) => {
      const startCount = this.received.length;
      const targetCount = startCount + count;

      if (this.received.length >= targetCount) {
        resolve(this.received.slice(startCount));
        return;
      }

      const timer = setTimeout(() => {
        this.inputCallback = undefined;
        reject(new Error(`Timeout waiting for ${count} values`));
      }, timeout);

      const originalCallback = this.inputCallback;
      this.inputCallback = (ep, val, time) => {
        originalCallback?.(ep, val, time);
        if (this.received.length >= targetCount) {
          clearTimeout(timer);
          this.inputCallback = originalCallback;
          resolve(this.received.slice(startCount));
        }
      };
    });
  }

  /**
   * Assert that a value was received
   */
  assertReceived(endpointId: string, expectedValue: any): boolean {
    const received = this.getReceivedOn(endpointId);
    return received.some(r => JSON.stringify(r.value) === JSON.stringify(expectedValue));
  }

  /**
   * Assert that the last received value matches
   */
  assertLastReceived(endpointId: string, expectedValue: any): boolean {
    const last = this.getLastReceivedOn(endpointId);
    return last !== undefined && JSON.stringify(last.value) === JSON.stringify(expectedValue);
  }
}

// ─────────────────────────────────────────────────────────────
// Node Definition
// ─────────────────────────────────────────────────────────────

export const testNodeDef: NodeDef<TestNode> = {
  type: 'Test',
  shortName: 'test',
  info: 'Captures inputs for testing and debugging. Echoes input to output.',
  domain: Domain.Data,
  endpoints,
  ctor: TestNode
};
